import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/shadcn/tooltip";
import MaterialIcon from "@/components/general/common/MaterialIcon";
import { ExportToExcelButton } from "@/components/general/common/ExportToExcelButton";
import { exportTournamentAnalysisToExcel } from "@/api/excelExport";
import type { Tournament } from "../../../api/tournaments";
import { useSelectedTeam } from "@/context/teamContext";

interface TournamentAnalysisHeaderProps {
    selectedTournament?: string;
    onTournamentChange?: (tournament: string) => void;
    selectedTournamentId?: number;
    tournaments?: Tournament[];
}

export function TournamentAnalysisHeader({
    selectedTournament,
    onTournamentChange,
    selectedTournamentId,
    tournaments,
}: TournamentAnalysisHeaderProps) {
    const { teamId, teamName } = useSelectedTeam();

    return (
        <TooltipProvider>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
                <div>
                    <h1 className="text-4xl font-black text-primary tracking-tight mb-2 font-headline">
                        Tournament Analysis
                    </h1>
                    <p className="text-on-surface-variant font-medium hidden lg:block">
                        {selectedTournament || "Select a tournament to begin analysis"}
                    </p>
                </div>

                <div className="flex flex-col gap-3 items-center w-full md:flex-row md:items-center md:w-auto">
                    <div id="onboarding-tournament-selector" className="w-full md:w-auto">
                        <Select
                            value={selectedTournament || ""}
                            onValueChange={(v) => onTournamentChange?.(v)}
                            disabled={!tournaments || tournaments.length === 0}
                        >
                            <SelectTrigger className="w-full md:w-fit bg-surface-container-highest border-0">
                                <div className="flex items-center gap-2">
                                    <MaterialIcon name="sort" className="text-primary text-lg" />
                                    <SelectValue placeholder="Select tournament" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                {tournaments && tournaments.length > 0 ? (
                                    tournaments.map((tournament) =>
                                        tournament.matches.length === 0 ? (
                                            <Tooltip key={tournament.id} delayDuration={0}>
                                                <TooltipTrigger asChild>
                                                    <div
                                                        className="flex items-center px-2 py-1.5 text-sm opacity-50 cursor-not-allowed text-on-surface"
                                                        onClick={(e) => e.preventDefault()}
                                                    >
                                                        <div className="flex items-baseline gap-2">
                                                            <span>{tournament.name}</span>
                                                            <span className="text-sm text-on-surface-variant">
                                                                {tournament.id}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent side="left">
                                                    <p>No data for {teamName} in this tournament</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        ) : (
                                            <SelectItem key={tournament.id} value={tournament.name}>
                                                <div className="flex items-baseline gap-2">
                                                    <span>{tournament.name}</span>
                                                    <span className="text-sm text-on-surface-variant">
                                                        {tournament.id}
                                                    </span>
                                                </div>
                                            </SelectItem>
                                        )
                                    )
                                ) : (
                                    <div className="px-3 py-2 text-on-surface-variant text-sm">
                                        No tournaments available
                                    </div>
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                    {selectedTournamentId && (
                        <div id="onboarding-tournament-export">
                            <ExportToExcelButton
                                mutationFn={() => exportTournamentAnalysisToExcel(selectedTournamentId!, teamId)}
                                label="Export Analysis to Excel"
                                successMessage="Tournament analysis Excel file downloaded successfully!"
                            />
                        </div>
                    )}
                </div>
            </div>
        </TooltipProvider>
    );
}
